/* eslint-disable max-len */
import React, { useRef, useState, useCallback, useMemo } from 'react'
import {
  ActionButton,
  ActionButtonIcons,
  Button,
} from '@enamelai/design-system'
import { Icon as AddIcon } from '@enamelai/design-system/icons/Add'
import { Icon as FileIcon } from '@enamelai/design-system/icons/File'
import { Patient } from '@dataTypes/PatientInfo.types'

type FormsStepProps = {
  patientData: Patient
  handleInputChange: (
    field: keyof Patient,
    value: Patient[keyof Patient],
  ) => void
  handleFileUpload: (files: FileList | null) => void
  handleDeleteFile: (index: number) => void
}

const FormsStep: React.FC<FormsStepProps> = ({
  patientData,
  handleFileUpload,
  handleDeleteFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB

  const ALLOWED_FILE_TYPES = useMemo(
    () => ['application/pdf', 'image/jpeg', 'image/png'],
    [],
  )

  const handleAddFormClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  const validateFiles = useCallback(
    (files: File[]): File[] => {
      const validFiles: File[] = []
      files.forEach((file) => {
        if (
          ALLOWED_FILE_TYPES.includes(file.type) &&
          file.size <= MAX_FILE_SIZE
        ) {
          validFiles.push(file)
        } else {
          const error =
            file.size > MAX_FILE_SIZE
              ? `File size exceeds 2MB: ${file.name}`
              : `Unsupported file type: ${file.name}`
          setErrorMessage(error)
        }
      })
      return validFiles
    },
    [ALLOWED_FILE_TYPES, MAX_FILE_SIZE],
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const files = e.target.files
      if (files) {
        const fileArray = Array.from(files)
        const validFiles = validateFiles(fileArray)
        if (validFiles.length > 0) {
          handleFileUpload(e.target.files)
        }
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFileUpload, validateFiles],
  )

  const formatFileSize = (size: number): string => {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB']
    let unitIndex = 0
    let formattedSize = size

    while (formattedSize >= 1024 && unitIndex < units.length - 1) {
      formattedSize /= 1024
      unitIndex++
    }

    return `${formattedSize.toFixed(1)} ${units[unitIndex]}`
  }

  return (
    <div className="add-patient-step">
      <p className="add-patient-forms__title">Add the necessary forms</p>

      {patientData.uploadedFiles && patientData.uploadedFiles.length > 0 && (
        <ul className="add-patient-files">
          {patientData.uploadedFiles.map((file, index) => (
            <li key={index} className="add-patient-files__item">
              <FileIcon
                className="add-patient-files__icon"
                aria-hidden="true"
              />
              <div className="add-patient-files__meta">
                <span className="add-patient-files__name">{file.name}</span>
                <span className="add-patient-files__size">
                  {formatFileSize(file.size)}
                </span>
              </div>
              <ActionButton
                icon={ActionButtonIcons.delete()}
                label={`Remove ${file.name}`}
                tone="danger"
                onClick={() => handleDeleteFile(index)}
              />
            </li>
          ))}
        </ul>
      )}

      {errorMessage && (
        <p className="add-patient-forms__error" role="alert">
          {errorMessage}
        </p>
      )}

      <div className="add-patient-forms__actions">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<AddIcon aria-hidden="true" />}
          onClick={handleAddFormClick}
        >
          Add form
        </Button>
        <input
          type="file"
          multiple
          hidden
          accept={ALLOWED_FILE_TYPES.join(',')}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </div>
    </div>
  )
}

export default React.memo(FormsStep)
